import { useEffect, useMemo, useState } from 'react';
import { subscribeSupplier, subscribeSuppliers } from '@/data/suppliersRepo';
import { withInheritedClosures, type ExperienceClosuresType } from '@/types/types_closure';
import { normaliseSupplierClosures, type SupplierType } from '@/types/types_supplier';

/** Subscribes to the whole supplier list in real time. */
export function useSuppliers() {
  const [suppliers, setSuppliers] = useState<SupplierType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return subscribeSuppliers((list) => {
      setSuppliers(list);
      setLoading(false);
    });
  }, []);

  return { suppliers, loading };
}

/**
 * One supplier by id, subscribed on its own rather than picked out of the list.
 *
 * The list subscription is the CMS's; this is what a page holding a single
 * experience wants — one document instead of the whole collection, and it
 * re-renders when somebody in the CMS adds an absence while a concierge has the
 * experience page open. `undefined` covers both "not loaded yet" and "no such
 * supplier", which is deliberate: every caller treats them the same.
 */
export function useSupplier(id: string | null | undefined) {
  // Stamped with the id it describes, the way `usePartnerById` does it: "still
  // loading" is then derived, and the supplier left by a previous id is never
  // read as this one.
  const [result, setResult] = useState<{ id: string; supplier?: SupplierType } | null>(null);

  useEffect(() => {
    if (!id) return;
    return subscribeSupplier(
      id,
      (loaded) => setResult({ id, supplier: loaded }),
      (e) => {
        // A supplier that cannot be read is one whose absences we do not know
        // about, which is the same position as an experience with no supplier.
        // Logged rather than thrown: it must not take the experience page down.
        console.error('Loading supplier failed', e);
        setResult({ id });
      },
    );
  }, [id]);

  const loaded = result?.id === id ? result : undefined;
  return { supplier: loaded?.supplier, loading: !!id && !loaded };
}

/**
 * An experience's closures with its supplier's absences folded in — what every
 * client-side closure question should be asked of.
 *
 * The one call site that must *not* use this is the supplier's own editor, which
 * is editing the source rather than reading the result.
 *
 * `loading` is worth having: until the supplier arrives, the merged closures are
 * the experience's own, which would show a day as bookable that the artisan is
 * away for. Callers that take a booking wait for it; callers that only decorate
 * a page need not.
 */
export function useExperienceClosures(
  experience: ExperienceClosuresType & { supplierId: string | null },
): { closures: ExperienceClosuresType; supplier: SupplierType | undefined; loading: boolean } {
  const { supplier, loading } = useSupplier(experience.supplierId);
  const inherited = useMemo(() => normaliseSupplierClosures(supplier), [supplier]);
  const closures = useMemo(
    () => withInheritedClosures(
      { closurePeriods: experience.closurePeriods, weekdayClosures: experience.weekdayClosures },
      inherited,
    ),
    [experience.closurePeriods, experience.weekdayClosures, inherited],
  );

  return { closures, supplier, loading };
}
